import { useImperativeHandle, useRef, useState, type Ref } from 'react';
import clsx from 'clsx';
import { getInitials } from '@/shared/utils/getInitials';
import type { MentionOption } from '../../lib/agent-mention';

export interface MentionListProps {
  items: readonly MentionOption[];
  command: (attrs: { id: string; label: string; type: 'agent' | 'user' }) => void;
}

export interface MentionListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

/**
 * Dropdown body for the @ mention suggestion. Pure React (mounted via
 * tiptap's ReactRenderer) so rows can carry caller-built avatar nodes —
 * entities/* cannot value-import other entities, so the avatar
 * component choice lives with the option builder, not here.
 *
 * State-reset contract: when the `items` array *identity* changes we remount
 * the inner list via `key`. That gives us the "reset selection when options
 * change, preserve it while typing within the same list" behavior without a
 * `useEffect` watching `items`. Callers memoize `items` per keystroke, so a
 * new keystroke produces a new reference and the highlight snaps back to 0.
 */
export interface MentionListPropsWithRef extends MentionListProps {
  /** React 19 ref-as-prop: exposes an imperative keyboard-nav handle. */
  ref?: Ref<MentionListHandle>;
}

export function MentionList({ items, command, ref }: MentionListPropsWithRef) {
  return <MentionListInner items={items} command={command} ref={ref} key={itemsKey(items)} />;
}

MentionList.displayName = 'MentionList';

// Best-effort stable key derived from the current items' ids. Two calls with
// the same array *contents* still remount if the caller passes a fresh array,
// which is the intended reset trigger (list of matches changed).
function itemsKey(items: readonly MentionOption[]): string {
  return items.map((i) => `${i.type}:${i.id}`).join('|');
}

interface MentionListInnerProps extends MentionListProps {
  ref?: Ref<MentionListHandle>;
}

function MentionListInner({ items, command, ref }: MentionListInnerProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Keyboard events arrive between renders (imperative handle), so the
  // current index lives in a ref; state only drives the highlight styling.
  const selectedIndexRef = useRef(0);

  const setSelected = (index: number) => {
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  const select = (index: number) => {
    const item = items.at(index);
    if (item) command({ id: item.id, label: item.label, type: item.type });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => {
      const count = Math.max(items.length, 1);
      if (event.key === 'ArrowDown') {
        setSelected((selectedIndexRef.current + 1) % count);
        return true;
      }
      if (event.key === 'ArrowUp') {
        setSelected((selectedIndexRef.current - 1 + count) % count);
        return true;
      }
      // Tab autocompletes too — with one (or a highlighted) option it fills
      // the mention instead of moving focus out of the composer.
      if (event.key === 'Enter' || event.key === 'Tab') {
        select(selectedIndexRef.current);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) return null;

  return (
    <div
      data-testid="mention-dropdown"
      className="flex max-h-60 min-w-48 flex-col gap-0.5 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
    >
      {items.map((item, index) => (
        <button
          key={`${item.type}:${item.id}`}
          type="button"
          className={clsx(
            'flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1 text-left text-sm',
            index === selectedIndex && 'bg-muted',
          )}
          // mousedown (not click) so the editor doesn't lose focus first.
          onMouseDown={(e) => {
            e.preventDefault();
            select(index);
          }}
          onMouseEnter={() => {
            setSelected(index);
          }}
        >
          {item.avatarSlot ?? (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium">
              {getInitials(item.label)}
            </span>
          )}
          <span className="truncate">
            {item.label}
            {item.labelSuffix && <span className="text-muted-foreground">{item.labelSuffix}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}
